import { useState, useEffect } from 'react';
import api from '../lib/api';
import { toast } from 'sonner';
import { Settings, Plus, Trash2, Bell } from 'lucide-react';

export default function MarginAlertSettings() {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [alerts, setAlerts] = useState([]);
  const [threshold, setThreshold] = useState('');
  const [enabled, setEnabled] = useState(true);
  const [newPartId, setNewPartId] = useState('');
  const [newThreshold, setNewThreshold] = useState('');
  const [parts, setParts] = useState([]);
  const [partSearch, setPartSearch] = useState('');
  const [tab, setTab] = useState('config');

  const fetchData = async () => {
    try {
      const [cfgRes, alertRes] = await Promise.all([
        api.get('/margin-alert-config'),
        api.get('/margin-alerts?status=all'),
      ]);
      setConfig(cfgRes.data);
      setThreshold(cfgRes.data.global_threshold_pct ?? 15);
      setEnabled(cfgRes.data.enabled !== false);
      setAlerts(alertRes.data);
    } catch { toast.error('Failed to load config'); }
    setLoading(false);
  };

  useEffect(() => { fetchData(); }, []);

  const handleSaveConfig = async () => {
    setSaving(true);
    try {
      await api.put('/margin-alert-config', { global_threshold_pct: Number(threshold), enabled });
      toast.success('Config saved');
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    finally { setSaving(false); }
  };

  const handleAddOverride = async () => {
    if (!newPartId || !newThreshold) { toast.error('Part and threshold required'); return; }
    try {
      await api.put(`/margin-alert-config/overrides/${newPartId}`, { threshold_pct: Number(newThreshold) });
      toast.success('Override added');
      setNewPartId(''); setNewThreshold(''); setPartSearch('');
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const handleRemoveOverride = async (partId) => {
    try {
      await api.delete(`/margin-alert-config/overrides/${partId}`);
      toast.success('Override removed');
      fetchData();
    } catch { toast.error('Failed'); }
  };

  const handleDismiss = async (alertId) => {
    try {
      await api.put(`/margin-alerts/${alertId}/dismiss`);
      toast.success('Dismissed');
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const searchParts = async (q) => {
    setPartSearch(q);
    if (q.length < 2) { setParts([]); return; }
    try {
      const res = await api.get(`/parts?search=${encodeURIComponent(q)}&limit=20`);
      setParts(res.data.parts || res.data);
    } catch {}
  };

  const overrides = config?.overrides || [];
  const openAlerts = alerts.filter(a => a.status === 'open');

  if (loading) return <div className="p-12 text-center text-slate-400"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900 mx-auto mb-3"></div>Loading...</div>;

  return (
    <div className="space-y-5" data-testid="margin-alert-settings-page">
      <div>
        <h2 className="font-heading font-bold text-slate-900 text-xl">Margin Alerts</h2>
        <p className="text-slate-500 text-sm mt-0.5">Configure thresholds and view active alerts</p>
      </div>

      <div className="flex gap-1 border-b border-slate-200">
        {[{ key: 'config', label: 'Settings', icon: Settings }, { key: 'alerts', label: `Alerts (${openAlerts.length})`, icon: Bell }].map(t => (
          <button key={t.key} onClick={() => setTab(t.key)} data-testid={`alert-tab-${t.key}`}
            className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-semibold border-b-2 transition-colors ${tab === t.key ? 'border-slate-900 text-slate-900' : 'border-transparent text-slate-400 hover:text-slate-600'}`}>
            <t.icon size={14} /> {t.label}
          </button>
        ))}
      </div>

      {tab === 'config' && (
        <div className="space-y-5">
          {/* Global config */}
          <div className="bg-white border border-slate-200 rounded-sm p-5">
            <h3 className="text-sm font-bold text-slate-800 mb-4">Global Threshold</h3>
            <div className="flex items-center gap-4">
              <div>
                <label className="text-xs font-bold uppercase text-slate-500 mb-1 block">Threshold (%)</label>
                <input type="number" value={threshold} onChange={e => setThreshold(e.target.value)} step="0.1" min="0" max="100"
                  data-testid="global-threshold-input"
                  className="w-32 border border-slate-300 rounded-sm h-9 px-3 text-sm font-mono focus:outline-none focus:ring-1 focus:ring-slate-900" />
              </div>
              <div>
                <label className="text-xs font-bold uppercase text-slate-500 mb-1 block">Enabled</label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={enabled} onChange={e => setEnabled(e.target.checked)}
                    data-testid="alerts-enabled-toggle"
                    className="h-4 w-4 rounded border-slate-300 text-slate-900" />
                  <span className="text-sm text-slate-600">{enabled ? 'Active' : 'Disabled'}</span>
                </label>
              </div>
              <button onClick={handleSaveConfig} disabled={saving} data-testid="save-config-btn"
                className="mt-4 px-4 h-9 bg-slate-900 text-white text-sm font-bold uppercase rounded-sm hover:bg-slate-800 disabled:opacity-50">
                {saving ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>

          {/* Per-part overrides */}
          <div className="bg-white border border-slate-200 rounded-sm p-5">
            <h3 className="text-sm font-bold text-slate-800 mb-4">Per-Part Overrides</h3>
            <div className="flex items-end gap-3 mb-4">
              <div className="flex-1">
                <label className="text-xs font-bold uppercase text-slate-500 mb-1 block">Part</label>
                <input type="text" placeholder="Search part..." value={partSearch} onChange={e => searchParts(e.target.value)}
                  className="w-full border border-slate-300 rounded-sm h-9 px-3 text-sm focus:outline-none" />
                {parts.length > 0 && partSearch.length >= 2 && (
                  <div className="border border-slate-200 rounded-sm max-h-32 overflow-y-auto bg-white mt-1 absolute z-10">
                    {parts.slice(0, 10).map(p => (
                      <div key={p.part_id} onClick={() => { setNewPartId(p.part_id); setPartSearch(`${p.part_number} — ${p.part_name}`); setParts([]); }}
                        className="px-3 py-1.5 text-xs cursor-pointer hover:bg-slate-50">
                        <span className="font-mono">{p.part_number}</span> — {p.part_name}
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div className="w-32">
                <label className="text-xs font-bold uppercase text-slate-500 mb-1 block">Threshold %</label>
                <input type="number" value={newThreshold} onChange={e => setNewThreshold(e.target.value)} step="0.1" placeholder="15"
                  className="w-full border border-slate-300 rounded-sm h-9 px-3 text-sm font-mono focus:outline-none" />
              </div>
              <button onClick={handleAddOverride} data-testid="add-override-btn"
                className="h-9 px-3 bg-slate-900 text-white text-sm font-bold rounded-sm hover:bg-slate-800">
                <Plus size={14} />
              </button>
            </div>
            {overrides.length === 0 ? (
              <p className="text-sm text-slate-400">No per-part overrides. All parts use the global threshold.</p>
            ) : (
              <table className="data-table w-full">
                <thead><tr>
                  <th className="text-left">Part ID</th>
                  <th className="text-right">Threshold %</th>
                  <th className="text-center">Remove</th>
                </tr></thead>
                <tbody>
                  {overrides.map(o => (
                    <tr key={o.part_id}>
                      <td className="font-mono text-sm text-slate-700">{o.part_id}</td>
                      <td className="text-right font-mono text-sm">{o.threshold_pct}%</td>
                      <td className="text-center">
                        <button onClick={() => handleRemoveOverride(o.part_id)} className="p-1 text-red-400 hover:bg-red-50 rounded-sm"><Trash2 size={13} /></button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}

      {tab === 'alerts' && (
        <div className="space-y-3">
          {alerts.length === 0 ? (
            <div className="bg-white border border-slate-200 rounded-sm p-8 text-center text-slate-400 text-sm">No margin alerts</div>
          ) : alerts.map(a => (
            <div key={a.margin_alert_id} className={`bg-white border rounded-sm p-4 ${a.status === 'open' ? 'border-red-200' : 'border-slate-200 opacity-60'}`}>
              <div className="flex items-start justify-between">
                <div>
                  <p className={`text-sm font-bold ${a.status === 'open' ? 'text-red-700' : 'text-slate-600'}`}>{a.title}</p>
                  <p className="text-xs text-slate-500 mt-1">{a.message}</p>
                  <p className="text-[10px] text-slate-400 mt-1">
                    {a.status === 'open' ? 'Open' : a.status === 'dismissed' ? `Dismissed by ${a.dismissed_by}` : `Resolved: ${a.resolved_reason}`}
                    {' · '}{new Date(a.created_at).toLocaleDateString('en-IN')}
                  </p>
                </div>
                {a.status === 'open' && (
                  <button onClick={() => handleDismiss(a.margin_alert_id)} data-testid={`dismiss-alert-${a.margin_alert_id}`}
                    className="px-3 py-1 text-xs font-semibold border border-slate-300 rounded-sm hover:bg-slate-50">Dismiss</button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
